import { listVendorsApi } from 'api/inventory';
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const VendorContext = createContext(null);

export const VendorProvider = ({ children }) => {
  const [vendors, setVendors] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const initialized = useRef(false);

  const loadInitial = useCallback(() => {
    if (initialized.current) return;
    setLoading(true);
    listVendorsApi({ sort: 'VENDOR_ASC', page: 1 })
      .then((result) => {
        setVendors(result.items ?? []);
        setTotalCount(result.totalCount ?? 0);
        setPage(1);
        initialized.current = true;
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const loadMore = useCallback(() => {
    const nextPage = page + 1;
    setLoadingMore(true);
    listVendorsApi({ sort: 'VENDOR_ASC', page: nextPage })
      .then((result) => {
        setVendors((prev) => [...prev, ...(result.items ?? [])]);
        setTotalCount(result.totalCount ?? 0);
        setPage(nextPage);
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }, [page]);

  const hasMore = vendors.length < totalCount;

  const contextValue = useMemo(
    () => ({ vendors, totalCount, loading, loadingMore, hasMore, loadInitial, loadMore }),
    [vendors, totalCount, loading, loadingMore, hasMore, loadInitial, loadMore]
  );

  return <VendorContext.Provider value={contextValue}>{children}</VendorContext.Provider>;
};

VendorProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useVendorContext = () => {
  const ctx = useContext(VendorContext);
  if (!ctx) throw new Error('useVendorContext must be used inside VendorProvider');
  return ctx;
};
